import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";

interface SimpleCalendarProps {
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  className?: string;
}

const GREEK_DAYS = ["Κυ", "Δε", "Τρ", "Τε", "Πε", "Πα", "Σα"];
const GREEK_MONTHS = [
  "Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος",
  "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"
];

export function SimpleCalendar({ selected, onSelect, disabled, className }: SimpleCalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(
    selected ? new Date(selected.getFullYear(), selected.getMonth(), 1) : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );

  // Keep visible month aligned with external selected date changes (e.g. quick chips)
  React.useEffect(() => {
    if (!selected) return;
    setCurrentMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [selected]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  
  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (disabled?.(date)) return;
    onSelect?.(date);
  };

  const isSelected = (day: number) => {
    if (!selected) return false;
    return (
      selected.getDate() === day &&
      selected.getMonth() === currentMonth.getMonth() &&
      selected.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const days = [];
  // Empty cells for days before month starts
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-10 min-h-[2.5rem]" aria-hidden />);
  }
  // Actual days — fixed row height (no aspect-square) so wide modals don’t inflate cell size
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const isDisabled = disabled?.(date);
    days.push(
      <button
        key={day}
        type="button"
        onClick={() => handleDateClick(day)}
        disabled={isDisabled}
        className={cn(
          "h-10 min-h-[2.5rem] w-full rounded-md text-sm transition-colors flex items-center justify-center text-foreground",
          isSelected(day) && "bg-primary text-primary-foreground font-semibold",
          !isSelected(day) && !isDisabled && "hover:bg-accent hover:text-accent-foreground",
          isToday(day) && !isSelected(day) && "border border-primary",
          isDisabled && "opacity-50 cursor-not-allowed text-muted-foreground"
        )}
      >
        {day}
      </button>
    );
  }

  return (
    <div
      className={cn(
        "w-full bg-background text-foreground p-3 rounded-md",
        className
      )}
    >
      {/* Header with month/year and navigation */}
      <div className="flex items-center justify-between mb-3 gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={previousMonth}
          className="h-9 w-9 shrink-0 text-foreground hover:bg-muted"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        <div className="truncate text-center text-base font-semibold text-foreground">
          {GREEK_MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={nextMonth}
          className="h-9 w-9 shrink-0 text-foreground hover:bg-muted"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {GREEK_DAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium leading-tight text-muted-foreground py-1.5"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days grid */}
      <div className="grid grid-cols-7 gap-1">{days}</div>
    </div>
  );
}

